import { useMarketplaceExternalId } from '../../../../../../../services/marketplace/externalId';
import { useGetLeadByExternalIdPhotos } from '../../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdPhotos';
import { GetLeadByExternalId200 } from '../../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalId';
import { getPhotoTimestamp } from '../../../photos/buildPhotoSlotMap';
import { DocumentStatus, useDocumentUpload } from '../useDocumentUpload';
import {
  CAR_PHOTOS_TODO,
  CONFIRM_ADDRESS_TODO,
  FALLBACK_UPLOAD_TODOS,
  PICKUP_AVAILABILITY_TODO,
  TodoDefinition,
  findChecklistSection,
  formatPickupAddress,
  isUploadTodo,
  pickupAnswerSummary,
  pickupAvailabilityState,
  uploadTodoDefinition,
} from '../utils';

export type TodoState = 'todo' | 'done' | 'onFile';

export type TodoItem = {
  definition: TodoDefinition;
  state: TodoState;
  doneNote?: string;
  thumbnailUrl?: string;
};

const documentTodoState = (status: DocumentStatus | undefined): TodoState => {
  if (status === 'uploaded') return 'done';
  if (status === 'onDeal') return 'onFile';
  return 'todo';
};

// Pickup-stage task, kept out of `todos` so it never counts toward Finish-stage progress.
const buildPickupTodo = (lead: GetLeadByExternalId200): TodoItem | null => {
  const answer = lead.pickupAvailability;
  if (answer != null) {
    return {
      definition: PICKUP_AVAILABILITY_TODO,
      state: 'done',
      doneNote: pickupAnswerSummary(answer),
    };
  }
  if (pickupAvailabilityState(lead) === 'ask') {
    return { definition: PICKUP_AVAILABILITY_TODO, state: 'todo' };
  }
  return null;
};

export const useTodoList = (dealId: string, lead: GetLeadByExternalId200) => {
  const externalId = useMarketplaceExternalId(dealId);
  const {
    checklist,
    checklistPhotos,
    statusByType,
    statusReady: documentsStatusReady,
    statusError,
    isProcessing: isDocumentProcessing,
    upload,
    isOdometerPromptOpen,
    confirmOdometerUpload,
    cancelOdometerUpload,
  } = useDocumentUpload(dealId);

  const photosQuery = useGetLeadByExternalIdPhotos(
    { externalId },
    { query: { enabled: !!externalId } },
  );
  // On photo fetch error, degrade to showing the photos todo instead of blocking the panel.
  const photosReady = !!photosQuery.data || photosQuery.isError;

  const vehicleShots = findChecklistSection(checklist, 'photos')?.shots;
  const shotsRemaining = (vehicleShots ?? []).filter((shot) => shot.status !== 'uploaded').length;
  const vehicleProgress = {
    complete: !!vehicleShots?.length && shotsRemaining === 0,
    remaining: shotsRemaining,
    total: vehicleShots?.length ?? 0,
  };
  const vehicleShotTypes = new Set<string>((vehicleShots ?? []).map((shot) => shot.photoType));

  const acceptedAtMs = lead.acceptedAt ? Date.parse(lead.acceptedAt) : Number.NaN;
  const anyShotUploadedAfterAcceptance =
    !Number.isNaN(acceptedAtMs) &&
    (photosQuery.data ?? []).some(
      (photo) => vehicleShotTypes.has(photo.photoType) && getPhotoTimestamp(photo) >= acceptedAtMs,
    );

  const photoThumbnailUrl = (photoType: string) =>
    (photosQuery.data ?? [])
      .filter((photo) => photo.photoType === photoType && !!photo.url)
      .sort((first, second) => getPhotoTimestamp(second) - getPhotoTimestamp(first))[0]?.url ??
    undefined;

  const buildTodo = (definition: TodoDefinition): TodoItem | null => {
    if (definition.key === 'confirm_address') {
      return findChecklistSection(checklist, 'confirmations')?.address.status === 'confirmed'
        ? { definition, state: 'done', doneNote: `Pickup at ${formatPickupAddress(lead)}` }
        : { definition, state: 'todo' };
    }
    if (definition.key === 'car_photos') {
      if (!photosReady || !vehicleProgress.complete) return { definition, state: 'todo' };
      // All shots existed before acceptance: the task was never needed, hide it.
      if (!anyShotUploadedAfterAcceptance) return null;
      return {
        definition,
        state: 'done',
        doneNote: `All ${vehicleProgress.total} shots in your photo set.`,
      };
    }
    const state = documentTodoState(statusByType[definition.key]);
    if (state === 'onFile') return { definition, state, doneNote: 'Already on file.' };
    if (state === 'todo') return { definition, state };
    return {
      definition,
      state,
      doneNote: 'Received.',
      thumbnailUrl:
        isUploadTodo(definition) && definition.kind === 'photo'
          ? photoThumbnailUrl(definition.key)
          : undefined,
    };
  };

  const documentItems = findChecklistSection(checklist, 'documents')?.items ?? [];
  const checklistTodos = [
    ...documentItems.map(({ documentType, label }) =>
      uploadTodoDefinition('document', documentType, label),
    ),
    ...checklistPhotos.map((photo) => {
      const definition = uploadTodoDefinition('photo', photo.photoType, photo.label);
      // Stale means the last upload aged past the server's freshness window,
      // so the todo reopens; explain why instead of showing the default help.
      if (photo.status === 'stale') {
        return {
          ...definition,
          help: `Your last photo is over ${photo.maxAgeHours} hours old. Take a fresh one, right off the dash.`,
        };
      }
      return definition;
    }),
  ];
  // On checklist fetch error with no cached data, degrade to the known set as open todos.
  const uploadTodos = statusError && !checklist ? FALLBACK_UPLOAD_TODOS : checklistTodos;
  const definitions = [CONFIRM_ADDRESS_TODO, ...uploadTodos, CAR_PHOTOS_TODO];

  const todos = definitions.map(buildTodo).filter((todo): todo is TodoItem => todo !== null);
  // Optional docs (required: false, e.g. registration) show as todos but don't gate progress.
  const optionalDocumentTypes = new Set<string>(
    documentItems.filter((document) => !document.required).map((document) => document.documentType),
  );
  const requiredTodos = todos.filter((todo) => !optionalDocumentTypes.has(todo.definition.key));
  const remaining = requiredTodos.filter((todo) => todo.state === 'todo').length;
  const statusReady = (documentsStatusReady || statusError) && photosReady;

  const isProcessing = (definition: TodoDefinition) =>
    isUploadTodo(definition) && isDocumentProcessing(definition.key);

  const uploadForTask = async (definition: TodoDefinition, file: File): Promise<void> => {
    if (!isUploadTodo(definition)) return;
    await upload(definition.kind, definition.key, file);
  };

  const uploadShot = async (photoType: string, file: File): Promise<void> => {
    await upload('photo', photoType, file);
  };

  return {
    lead,
    externalId,
    todos,
    pickupTodo: buildPickupTodo(lead),
    remaining,
    total: requiredTodos.length,
    statusReady,
    isFinished: statusReady && remaining === 0,
    showCarPhotosPanel: photosReady && vehicleProgress.complete,
    vehicleShots,
    vehicleProgress,
    isProcessing,
    isShotProcessing: isDocumentProcessing,
    photoThumbnailUrl,
    uploadForTask,
    uploadShot,
    isOdometerPromptOpen,
    confirmOdometerUpload,
    cancelOdometerUpload,
  };
};

export type TodoList = ReturnType<typeof useTodoList>;
